/**
 * AppmetricaIntegrationPage — Mobile Analytics Integration config.
 * Single API token form + postback URL config + recent event stream status.
 * AppMetrica branding is allowed once (feature title); elsewhere uses "Mobile Analytics Integration".
 * Green accent, neutral zinc borders. No emerald/blue/orange/purple.
 */
import { useMemo, useState, type FormEvent } from "react";
import {
	useAppmetricaIntegrationCreate,
	useAppmetricaIntegrationList,
} from "./hooks";

const CARD = "rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm";
const LABEL = "text-sm font-medium text-zinc-700";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-600/20";
const BTN =
	"rounded-[6px] border border-green-700 bg-green-700 px-4 py-2 text-sm font-medium text-white hover:bg-green-800 disabled:cursor-not-allowed disabled:opacity-60";

const EVENT_OPTIONS = ["install", "open", "session_start", "revenue", "custom"];

function isValidHttpsUrl(raw: string): boolean {
	if (!raw) return false;
	try {
		const u = new URL(raw);
		return u.protocol === "https:" && !!u.host;
	} catch {
		return false;
	}
}

export function AppmetricaIntegrationPage() {
	const createMut = useAppmetricaIntegrationCreate();
	const listQ = useAppmetricaIntegrationList();

	const [apiToken, setApiToken] = useState("");
	const [postbackUrl, setPostbackUrl] = useState("");
	const [eventName, setEventName] = useState(EVENT_OPTIONS[0]);
	const [saved, setSaved] = useState(false);
	const [urlError, setUrlError] = useState<string | null>(null);

	const items = listQ.data?.items ?? [];
	const isLoading = listQ.isPending;
	const isEmpty = !isLoading && items.length === 0;

	const recent = useMemo(() => items.slice(0, 5), [items]);

	const handleSubmit = (e: FormEvent) => {
		e.preventDefault();
		setSaved(false);
		setUrlError(null);

		const token = apiToken.trim();
		const url = postbackUrl.trim();

		if (!token) return;
		if (!isValidHttpsUrl(url)) {
			setUrlError("Postback URL must be a valid https:// URL");
			return;
		}

		const eventId = `cfg_${Date.now()}`;
		createMut.mutate(
			{
				event_id: eventId,
				event_name: eventName,
				appmetrica_api_key: token,
				postback_url: url,
			},
			{
				onSuccess: () => {
					setSaved(true);
					setApiToken("");
					setPostbackUrl("");
				},
			},
		);
	};

	return (
		<main
			data-testid="appmetrica-integration-page"
			className="mx-auto flex w-full max-w-3xl flex-col gap-8 px-6 py-10"
		>
			<header className="flex flex-col gap-2">
				<h1 className="text-3xl font-semibold text-green-700">
					AppMetrica Integration
				</h1>
				<p className="text-sm text-zinc-600">
					Mobile Analytics Integration configuration. Connect the Yandex ingest
					partner to forward install, open, session and revenue events.
				</p>
			</header>

			<form
				onSubmit={handleSubmit}
				className={`${CARD} flex flex-col gap-4`}
				data-testid="appmetrica-integration-form"
				aria-label="Mobile Analytics Integration configuration form"
			>
				<div className="flex flex-col gap-1.5">
					<label htmlFor="appmetrica-integration-token" className={LABEL}>
						API Token
					</label>
					<input
						id="appmetrica-integration-token"
						type="password"
						autoComplete="off"
						required
						className={INPUT}
						value={apiToken}
						onChange={(e) => setApiToken(e.currentTarget.value)}
						data-testid="appmetrica-integration-token-input"
						placeholder="Paste Mobile Analytics API token"
					/>
				</div>

				<div className="flex flex-col gap-1.5">
					<label htmlFor="appmetrica-integration-postback" className={LABEL}>
						Postback URL
					</label>
					<input
						id="appmetrica-integration-postback"
						type="url"
						required
						className={INPUT}
						value={postbackUrl}
						onChange={(e) => setPostbackUrl(e.currentTarget.value)}
						data-testid="appmetrica-integration-postback-input"
						placeholder="https://example.com/postback"
					/>
					{urlError ? (
						<p
							className="text-xs text-red-600"
							role="alert"
							data-testid="appmetrica-integration-url-error"
						>
							{urlError}
						</p>
					) : null}
				</div>

				<div className="flex flex-col gap-1.5">
					<label htmlFor="appmetrica-integration-event" className={LABEL}>
						Event Name
					</label>
					<select
						id="appmetrica-integration-event"
						className={INPUT}
						value={eventName}
						onChange={(e) => setEventName(e.currentTarget.value)}
						data-testid="appmetrica-integration-event-select"
					>
						{EVENT_OPTIONS.map((opt) => (
							<option key={opt} value={opt}>
								{opt}
							</option>
						))}
					</select>
				</div>

				<div className="flex items-center gap-3">
					<button
						type="submit"
						className={BTN}
						disabled={createMut.isPending}
						data-testid="appmetrica-integration-save"
					>
						{createMut.isPending ? "Saving." : "Save Configuration"}
					</button>
					{saved ? (
						<p
							className="text-sm text-green-700"
							role="status"
							data-testid="appmetrica-integration-saved"
						>
							Configuration saved.
						</p>
					) : null}
					{createMut.error ? (
						<p className="text-sm text-red-600" role="alert">
							{createMut.error.message}
						</p>
					) : null}
				</div>
			</form>

			<section
				className={`${CARD} flex flex-col gap-3`}
				data-testid="appmetrica-integration-stream"
				aria-label="Event stream status"
			>
				<header className="flex items-center justify-between">
					<h2 className="text-base font-semibold text-zinc-900">
						Event Stream Status
					</h2>
					<span
						className="rounded-full border border-zinc-300 bg-zinc-50 px-2 py-0.5 text-xs text-zinc-700"
						data-testid="appmetrica-integration-count"
					>
						{items.length} recent
					</span>
				</header>

				{isLoading ? (
					<p
						className="text-sm text-zinc-600"
						data-testid="appmetrica-integration-loading"
					>
						Loading stream.
					</p>
				) : null}

				{isEmpty ? (
					<p
						className="text-sm text-zinc-600"
						data-testid="appmetrica-integration-empty"
					>
						No events yet. Save a configuration, then send a test event from the
						Mobile Analytics Integration console.
					</p>
				) : null}

				{!isLoading && recent.length > 0 ? (
					<ul
						className="flex flex-col divide-y divide-zinc-200"
						data-testid="appmetrica-integration-list"
					>
						{recent.map((row) => {
							const r = row as {
								id: string | number;
								event_id?: string;
								event_name?: string;
								created_at?: number;
							};
							return (
								<li
									key={String(r.id)}
									className="flex items-center justify-between py-2 text-sm"
								>
									<span className="font-mono text-zinc-700">
										{r.event_id ?? String(r.id)}
									</span>
									<span className="text-zinc-600">{r.event_name ?? "-"}</span>
									<span className="text-xs text-zinc-500">
										{r.created_at
											? new Date(r.created_at * 1000).toISOString()
											: "-"}
									</span>
								</li>
							);
						})}
					</ul>
				) : null}
			</section>
		</main>
	);
}

export default AppmetricaIntegrationPage;
